/**
 * Import Progress Indicator — the live/connecting/offline chip.
 *
 * Split out of ImportProgressIndicator.tsx to keep that file under the
 * 500-line LLM ceiling (CLAUDE.md §0). Presentational only.
 *
 * "Sin conexión" is honest, not fatal: since completion is polled from
 * `/imports/sync/status` the panel keeps working with the stream down — the
 * chip tells the user updates arrive a few seconds later, nothing more.
 */

import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import type { ConnectionState } from '@/platform/websocket/syncClient'

interface ImportPanelConnectionStateProps {
  connectionState: ConnectionState
}

export const ImportPanelConnectionState = ({
  connectionState,
}: ImportPanelConnectionStateProps) => {
  const { t } = useTranslation('imports')

  switch (connectionState) {
    case 'connected':
      return (
        <span className="flex items-center gap-1 text-xs text-crypto-success">
          <span className="w-2 h-2 bg-crypto-success rounded-full motion-safe:animate-pulse" aria-hidden="true" />
          {t('progressIndicator.live', 'Live')}
        </span>
      )
    case 'connecting':
      return (
        <span className="flex items-center gap-1 text-xs text-crypto-warning">
          <Loader2 className="w-3 h-3 motion-safe:animate-spin" aria-hidden="true" />
          {t('progressIndicator.connecting', 'Connecting...')}
        </span>
      )
    default:
      return (
        <span className="flex items-center gap-1 text-xs text-crypto-muted">
          <span className="w-2 h-2 bg-crypto-muted rounded-full" aria-hidden="true" />
          {t('progressIndicator.offline', 'Offline')}
        </span>
      )
  }
}
